import { Field, Input, Textarea } from '@/design-system';

import { surveyCopy } from '../copy';
import { useDraftText } from '../hooks/useDraftText';
import type { QuestionView } from '../model/view';

export interface ValueAnswerProps {
    view: QuestionView;
    labelledBy: string;
    readOnly?: boolean;
    onCommit: (raw: string) => void;
}

/** `12.5` → `12,5`: Nederlandse notatie in het veld. */
function formatNumber(value: number | null | undefined): string {
    return value === null || value === undefined ? '' : String(value).replace('.', ',');
}

/**
 * Het antwoord op een getal- of tekstvraag. Het veld schrijft pas bij `blur`:
 * één bewerking in de outbox per ingevuld getal, niet één per toetsaanslag.
 * De velden zijn 44 px hoog, net als elke optierij.
 */
export function ValueAnswer({ view, labelledBy, readOnly = false, onCommit }: ValueAnswerProps) {
    const question = view.question;
    const external =
        question.type === 'number'
            ? formatNumber(view.answer?.value_number)
            : (view.answer?.value_text ?? '');
    const [text, setText] = useDraftText(external);

    const commit = () => {
        if (text !== external) {
            onCommit(text);
        }
    };

    if (question.type === 'number') {
        return (
            <div className="px-3 py-[10px]">
                <Field
                    label={question.number_unit ?? surveyCopy.question.numberLabel}
                    description={question.help_text ?? undefined}
                >
                    <Input
                        mono
                        inputMode="decimal"
                        aria-labelledby={labelledBy}
                        value={text}
                        readOnly={readOnly}
                        onChange={(event) => setText(event.target.value)}
                        onBlur={commit}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.currentTarget.blur();
                            }
                        }}
                    />
                </Field>
            </div>
        );
    }

    return (
        <div className="px-3 py-[10px]">
            <Field
                label={surveyCopy.question.textLabel}
                description={
                    question.type === 'text_items'
                        ? surveyCopy.question.textItemsHint
                        : (question.help_text ?? undefined)
                }
            >
                <Textarea
                    aria-labelledby={labelledBy}
                    value={text}
                    readOnly={readOnly}
                    onChange={(event) => setText(event.target.value)}
                    onBlur={commit}
                />
            </Field>
        </div>
    );
}
